/**
 * SmartInstallBanner
 * ──────────────────
 * Slim, dismissible banner that suggests the appropriate app store to
 * mobile-web visitors. Auto-detects iOS vs Android via
 * `useInstallPrompt` and hides itself for Despia native users +
 * desktop.
 *
 * UX rules:
 *   • Appears in-flow (not as a fixed overlay) so it never blocks
 *     content — feels like a native "Smart App Banner" without the
 *     iOS Safari implementation quirks.
 *   • Slides in on mount with a subtle transition.
 *   • Dismissal writes a 7-day cooldown to localStorage; won't reappear
 *     on the same device inside that window.
 *   • Session cap of 3 impressions total to prevent nagging even before
 *     an explicit dismiss.
 *
 * Placement: drop `<SmartInstallBanner />` at the top of any surface
 * where a soft install prompt makes sense (landing hero, reflection
 * page top, community feed header). The hook centralises cooldown so
 * multiple placements coordinate without extra wiring.
 */

import React from 'react';
import { X, Download } from 'lucide-react';
import useInstallPrompt, { buildStoreUrl } from '@/hooks/useInstallPrompt';

import { useTranslation } from 'react-i18next';

/**
 * Fire the install-click tracking beacon before navigation. Mirrored
 * from AppStoreBadges.jsx — kept inline (rather than shared util) to
 * avoid a util file for two callers. Silent failure — never blocks
 * the outbound navigation.
 */
function trackInstallClick(source, store) {
  try {
    const payload = JSON.stringify({ source, store });
    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/install/track`;
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch { /* swallow */ }
}

const APPLE_ICON = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);
const PLAY_ICON = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path fill="#00d1ff" d="M3.5 3.5v17l7-8.5-7-8.5z" opacity="0.9" />
    <path fill="#0af" d="M3.5 20.5l10-6-3-2.5-7 8.5z" opacity="0.85" />
    <path fill="#fbbf24" d="M13.5 14.5l5.5-2c1-.4 1-1.6 0-2l-5.5-2-3 2.5 3 3.5z" />
    <path fill="#ef4444" d="M3.5 3.5l10 6-3 2.5-7-8.5z" opacity="0.9" />
  </svg>
);

export default function SmartInstallBanner({ className = '', source = 'banner', sessionCap }) {
  const {
    t
  } = useTranslation();

  const { visible, target, dismiss } = useInstallPrompt(
    sessionCap ? { sessionCap } : {}
  );

  if (!visible || !target) return null;

  const isIOS = target.store === 'app-store';
  const brand = isIOS ? 'App Store' : 'Google Play';
  const icon = isIOS ? APPLE_ICON : PLAY_ICON;
  const trackedUrl = buildStoreUrl(target.url, target.store, source);

  return (
    <div
      className={`w-full rounded-2xl px-4 py-3 flex items-center gap-3 ${className}`}
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        backdropFilter: 'blur(12px)',
      }}
      role="region"
      aria-label={`Install Eili on ${target.platformLabel}`}
      data-testid="smart-install-banner"
    >
      <div
        className="p-2 rounded-xl shrink-0"
        style={{ background: 'var(--glass-bg-active, #334155)', color: 'var(--theme-text)' }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-semibold truncate"
          style={{ color: 'var(--theme-text)' }}
        >
          {t('generated.smartinstallbanner_eili_works_great_b652')}
        </p>
        <p
          className="text-xs truncate"
          style={{ color: 'var(--theme-textSecondary)' }}
        >
          {t('generated.smartinstallbanner_faster_offline_ready_428a')} {target.platformLabel}.
                  </p>
      </div>
      <a
        href={trackedUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackInstallClick(source, target.store)}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition hover:opacity-90 active:scale-[0.97]"
        style={{
          background: 'var(--theme-accent)',
          color: 'var(--theme-accent-contrast, #0f172a)',
        }}
        data-testid="smart-install-cta"
      >
        <Download className="h-3.5 w-3.5" />
        {t('generated.smartinstallbanner_get_on_d3bc')} {brand}
      </a>
      <button
        onClick={dismiss}
        className="shrink-0 p-1.5 rounded-full transition hover:bg-white/10"
        aria-label={t('generated.smartinstallbanner_dismiss_install_suggestion_a937')}
        data-testid="smart-install-dismiss"
      >
        <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
      </button>
    </div>
  );
}
